<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { UpgradeToGoldVariant } from '@/core/shared/dialog/dialog.types';
import { CustomerSuccessService } from '@/core/customer-success/customer-success.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import SubscriptionService from '@/core/shared/subscription/subscription.service';
import { useSubscriptionV2Store } from '@/core/shared/subscription/subscription-v2.store';
import BaseModal from '@/components/Shared/Modals/BaseModal.vue';
import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';

const dialogService = new DialogService();
const customerSuccessService = new CustomerSuccessService();
const subscriptionService = SubscriptionService.instance;
const subscriptionV2Store = useSubscriptionV2Store();

const variantMap = {
  [UpgradeToGoldVariant.TalentSearch]: {
    title: 'Access 11M+ skilled candidates',
    subtitle: 'Unlock and directly contact qualified local candidates.',
    secondFeature: {
      title: 'Labor Market Reports',
      description:
        'Get deep insights into hiring trends, salary benchmarks, and talent availability in your area.',
    },
  },
  [UpgradeToGoldVariant.LaborMarket]: {
    title: 'Access Unlimited Labor Market Reports',
    subtitle: 'Access hiring trends, salary benchmarks, and local talent data.',
    secondFeature: {
      title: 'Full Talent Pool Access',
      description:
        'Unlock contact details and reach out directly to qualified candidates in your area.',
    },
  },
  [UpgradeToGoldVariant.InterviewScheduling]: {
    title: 'Access AI-Powered Interview Scheduling',
    subtitle: 'AI-powered automation for outreach and interview scheduling.',
    secondFeature: {
      title: 'Labor Market Reports',
      description:
        'Get deep insights into hiring trends, salary benchmarks, and talent availability in your area.',
    },
  },
};

const fixedFeatures = {
  first: {
    description: 'Save on job postings to maximize your hiring reach at a discounted rate.',
  },
  third: {
    title: 'AI-Powered Interview Scheduling',
    description:
      'Let your AI recruiter automatically book interviews with high-scoring candidates who complete screening.',
  },
  fourth: {
    title: 'ATS Integrations (optional)',
    description: 'Seamlessly sync with your existing hiring tools and maintain your workflow.',
  },
};

const isUpgrading = ref(false);
const isLoadingProposal = ref(false);

const isOpen = computed(() => dialogService.upgradeToGoldModalProps?.isOpen || false);
const variant = computed(
  () => dialogService.upgradeToGoldModalProps?.variant || UpgradeToGoldVariant.LaborMarket,
);

const modalContent = computed(() => {
  return variantMap[variant.value];
});

const discountPercent = computed(() => upgradeProposal.value?.savings?.percent ?? 0);

const goldFeatures = computed(() => {
  return [
    {
      title: `${discountPercent.value}% Off Job Slots`,
      description: fixedFeatures.first.description,
    },
    modalContent.value.secondFeature,
    fixedFeatures.third,
    fixedFeatures.fourth,
  ];
});

const upgradeProposal = computed(() => subscriptionV2Store.upgradeProposal);
const currentPlan = computed(() => upgradeProposal.value?.current);
const proposedPlan = computed(() => upgradeProposal.value?.proposed);
const savings = computed(() => upgradeProposal.value?.savings);

const showDynamicPricing = computed(() => {
  return !!(currentPlan.value?.monthlyAmount && proposedPlan.value?.monthlyAmount);
});

function formatCurrency(amountInCents: number, currency: string): string {
  const amount = amountInCents / 100;
  const hasDecimals = amountInCents % 100 !== 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

const currentPlanLabel = computed(() => {
  if (!currentPlan.value) return '';
  return `${currentPlan.value.planName} (current plan)`;
});

const proposedPlanLabel = computed(() => {
  if (!proposedPlan.value) return '';
  return `${proposedPlan.value.planName} (upgrade)`;
});

const formattedCurrentPrice = computed(() => {
  if (!currentPlan.value?.monthlyAmount) return '';
  return `${formatCurrency(currentPlan.value.monthlyAmount, currentPlan.value.currency)}/mo`;
});

const formattedProposedPrice = computed(() => {
  if (!proposedPlan.value?.monthlyAmount) return '';
  return `${formatCurrency(proposedPlan.value.monthlyAmount, proposedPlan.value.currency)}/mo`;
});

const formattedMembershipPrice = computed(() =>
  formattedProposedPrice.value.replace('/mo', '/month'),
);

const formattedSavings = computed(() => {
  if (!savings.value?.monthlyAmount || !currentPlan.value) return '';
  return `${formatCurrency(savings.value.monthlyAmount, currentPlan.value.currency)}/mo`;
});

watch(isOpen, async (newValue) => {
  if (newValue) {
    isLoadingProposal.value = true;
    try {
      await subscriptionService.fetchUpgradeProposal();
    } catch {
      subscriptionV2Store.upgradeProposal = null;
      SnackbarService.critical('Failed to load pricing information. Please try again later.');
    } finally {
      isLoadingProposal.value = false;
    }
  }
});

function handleClose() {
  if (dialogService.upgradeToGoldModalProps) {
    dialogService.upgradeToGoldModalProps.isOpen = false;
  }
  isUpgrading.value = false;
}

function handleMaybeLater() {
  handleClose();
}

async function handleUpgradeToGold() {
  if (isUpgrading.value) {
    return;
  }

  isUpgrading.value = true;

  try {
    const response = await customerSuccessService.submitGoldUpgradeRequest();

    if (response?.success) {
      handleClose();
      dialogService.upgradeToGoldModalProps.hasRequestedGold = true;
      dialogService.goldUpgradeConfirmedModalProps = {
        isOpen: true,
      };
    } else {
      SnackbarService.critical('Failed to submit upgrade request. Please try again later.');
    }
  } catch (error) {
    SnackbarService.critical('Failed to submit upgrade request. Please try again later.');
  } finally {
    isUpgrading.value = false;
  }
}
</script>

<template>
  <BaseModal
    :model-value="isOpen"
    :show-heading="false"
    :show-actions="false"
    max-width="640px"
    max-height="auto"
    @cancel="handleClose"
  >
    <!-- Close button -->
    <div class="absolute right-8 top-8">
      <button
        @click="handleClose"
        class="text-gray-500 hover:text-gray-700 transition-colors"
        aria-label="Close"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15 5L5 15M5 5L15 15"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <!-- Loading state -->
    <div v-if="isLoadingProposal" class="flex items-center justify-center py-20">
      <SpinnerLoader />
    </div>

    <!-- Content -->
    <div v-else class="">
      <!-- Badge -->
      <div class="mb-4 flex">
        <span
          class="rounded-full bg-gradient-to-r from-gold via-gold-light to-gold px-2 py-1 text-[10px] font-bold leading-[16px] text-shade-880"
        >
          Access with FactoryFix Gold
        </span>
      </div>

      <!-- Dynamic Title -->
      <h2 class="mb-2 font-serif text-2xl font-extrabold text-highlight-1000">
        {{ modalContent.title }}
      </h2>

      <!-- Dynamic Description -->
      <p class="mb-6 text-sm text-shade-820">
        {{ modalContent.subtitle }}
      </p>

      <!-- Features list -->
      <div class="mb-6 space-y-1">
        <!-- Feature items using v-for -->
        <div v-for="feature in goldFeatures" :key="feature.title" class="flex items-start py-2">
          <div class="mt-0.5 flex-shrink-0">
            <!-- Checkmark icon matching the design -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
            >
              <circle cx="8.5" cy="8.23828" r="8" fill="currentColor" class="text-gold" />
              <path
                d="M4.025568 8.05494L5.22727 6.84898L7.20028 8.7879L11.5767 4.42852L12.7869 5.63449L7.20028 11.1913L4.025568 8.05494Z"
                fill="black"
              />
            </svg>
          </div>
          <div class="ml-2">
            <h3 class="font-bold text-shade-900">{{ feature.title }}</h3>
            <p class="mt-1 text-sm text-shade-820">
              {{ feature.description }}
            </p>
          </div>
        </div>
      </div>

      <!-- Pricing section -->
      <div v-if="showDynamicPricing" class="mb-6">
        <!-- Plan comparison -->
        <div
          class="flex flex-col gap-3 rounded-t-2xl border border-b-0 border-gold bg-gold-bg-hover px-4 py-3 text-sm text-caution-900"
        >
          <div class="flex items-center justify-between">
            <span>{{ currentPlanLabel }}</span>
            <span class="font-bold">{{ formattedCurrentPrice }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span>{{ proposedPlanLabel }}</span>
            <span class="font-bold">{{ formattedProposedPrice }}</span>
          </div>
        </div>
        <!-- Savings highlight -->
        <div
          class="flex items-center justify-between rounded-b-2xl border border-gold bg-gradient-to-r from-gold to-gold-light px-4 py-2 text-lg font-extrabold text-shade-900"
        >
          <span>You save:</span>
          <span>{{ formattedSavings }}</span>
        </div>
      </div>

      <!-- Disclaimer -->
      <p class="mb-6 text-xs leading-[21px] text-shade-900">
        <span class="font-bold">Yes, upgrade me to FactoryFix Gold. </span>
        <span
          >I understand this is a 12-month commitment at {{ formattedMembershipPrice }}, and I agree
          to the FactoryFix terms and conditions.</span
        >
      </p>

      <!-- Action buttons -->
      <div class="flex items-center justify-end gap-3">
        <button
          @click="handleMaybeLater"
          class="px-4 py-[10px] text-sm font-semibold text-highlight-1000"
        >
          Maybe later
        </button>
        <button
          @click="handleUpgradeToGold"
          :disabled="isUpgrading"
          :class="[
            'min-w-[140px] rounded-lg px-4 py-[10px] text-sm font-semibold transition-colors',
            !isUpgrading ? 'bg-gold text-shade-900' : 'cursor-not-allowed bg-tint-80 text-tint-400',
          ]"
        >
          <span v-if="isUpgrading" class="flex items-center justify-center gap-2">
            <SpinnerLoader class="!h-4 !w-4" />
            Upgrading...
          </span>
          <span v-else>Upgrade to Gold</span>
        </button>
      </div>
    </div>
  </BaseModal>
</template>
